import { useRef, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { useFrame, RootState } from '@react-three/fiber';

interface TemperatureHazeProps {
  temperature: number;
}

// Temperature ranges and their corresponding colors
const TEMP_RANGES = {
  cold: { min: -Infinity, max: 10, color: new THREE.Color('#4dabf7'), intensity: 0.5 },
  cool: { min: 10, max: 20, color: new THREE.Color('#69db7c'), intensity: 0.6 },
  normal: { min: 20, max: 30, color: new THREE.Color('#ffd43b'), intensity: 0.7 },
  warm: { min: 30, max: 40, color: new THREE.Color('#ff9b'), intensity: 0.8 },
  hot: { min: 40, max: Infinity, color: new THREE.Color('#fa5252'), intensity: 0.9 },
};

export function TemperatureHaze({ temperature }: TemperatureHazeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const lightRef = useRef<THREE.PointLight>(null);

  // Determine temperature range and get corresponding color
  const tempRange = useMemo(() => {
    for (const [range, config] of Object.entries(TEMP_RANGES)) {
      if (temperature >= config.min && temperature < config.max) {
        return { range, ...config };
      }
    }
    return { range: 'normal', ...TEMP_RANGES.normal };
  }, [temperature]);

  // Create radial gradient texture
  const gradientTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Calculate center intensity based on temperature range
    const centerIntensity = tempRange.intensity;
    const midIntensity = centerIntensity * 0.6;
    const outerIntensity = centerIntensity * 0.2;

    const gradient = ctx.createRadialGradient(
      64, 64, 0,    // Inner circle center and radius
      64, 64, 64    // Outer circle center and radius
    );
    gradient.addColorStop(0, `rgba(255, 255, 255, ${centerIntensity})`);    // Center
    gradient.addColorStop(0.2, `rgba(255, 255, 255, ${midIntensity})`);     // Quick falloff
    gradient.addColorStop(0.4, `rgba(255, 255, 255, ${outerIntensity})`);   // Medium
    gradient.addColorStop(0.6, `rgba(255, 255, 255, ${outerIntensity * 0.5})`); // Faint
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');                     // Edge

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 128, 128);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }, [tempRange.intensity]);

  // Create material with gradient
  const material = useMemo(() => {
    if (!gradientTexture) return null;
    
    return new THREE.MeshBasicMaterial({
      color: tempRange.color,
      map: gradientTexture,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
  }, [tempRange.color, gradientTexture]);

  // Memoize the frame update callback
  const updateFrame = useCallback((state: RootState) => {
    if (meshRef.current && meshRef.current.material instanceof THREE.MeshBasicMaterial) {
      const time = state.clock.getElapsedTime();
      meshRef.current.material.opacity = 0.7 + 0.1 * Math.sin(time * 0.3);
    }
  }, []);

  // Use the memoized callback in useFrame
  useFrame(updateFrame);

  if (!material) return null;

  return (
    <group>
      <mesh ref={meshRef}>
        <sphereGeometry args={[2.5, 24, 24]} />
        <primitive object={material} />
      </mesh>
      <pointLight
        ref={lightRef}
        color={tempRange.color}
        intensity={tempRange.intensity * 2}
        distance={3}
        decay={1.5}
      />
    </group>
  );
} 